import { useState, useEffect, useCallback } from 'react';
import api from '../components/wrapper/API';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export function usePushNotifications() {
  const [isManager, setIsManager] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);

    const playerId = localStorage.getItem('playerId');
    if (!playerId || playerId === '0') {
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Abonnieren darf jeder Orga/Admin (Spieltagleiter-Pushes). isManager steuert nur, welche
    // Benachrichtigungen das Popup zusätzlich auflistet — gefiltert wird serverseitig beim Versand.
    api.get(`/User/GetForPlayer/${playerId}`)
      .then(res => { if (!cancelled) setIsManager(res.data.isRegistrationManager === true); })
      .catch(() => {});

    const subscriptionCheck = supported
      ? navigator.serviceWorker.ready
          .then(reg => reg.pushManager.getSubscription())
          .then(sub => { if (!cancelled) setIsSubscribed(sub !== null); })
          .catch(() => {})
      : Promise.resolve();
    subscriptionCheck.finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const subscribe = useCallback(async () => {
    setError(null);
    try {
      if (Notification.permission === 'denied') {
        setError('Benachrichtigungen sind in deinem Browser blockiert. Bitte in den Browser-Einstellungen aktivieren.');
        return false;
      }

      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          setError('Benachrichtigungen wurden nicht erlaubt.');
          return false;
        }
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      const key = subscription.getKey('p256dh');
      const auth = subscription.getKey('auth');

      await api.post('/Push/Subscribe', {
        endpoint: subscription.endpoint,
        p256dh: btoa(String.fromCharCode(...new Uint8Array(key))),
        auth: btoa(String.fromCharCode(...new Uint8Array(auth))),
        deviceLabel: navigator.userAgent.substring(0, 200)
      });

      setIsSubscribed(true);
      return true;
    } catch (err) {
      setError('Aktivierung fehlgeschlagen. Bitte versuche es erneut.');
      return false;
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setError(null);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        setIsSubscribed(false);
        return true;
      }

      await api.delete('/Push/Unsubscribe', { data: { endpoint: subscription.endpoint } });
      await subscription.unsubscribe();
      setIsSubscribed(false);
      return true;
    } catch (err) {
      setError('Deaktivierung fehlgeschlagen. Bitte versuche es erneut.');
      return false;
    }
  }, []);

  return { isManager, isSupported, isSubscribed, subscribe, unsubscribe, loading, error, setError };
}
